import { Tooltip } from "primereact/tooltip";
import React from "react";

const WeekView = ({ date, tasks, onTaskClick }) => {
  // Get the first day of the week (Sunday) for the given date
  const getFirstDayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  // Generate an array of 7 days starting from the first day of the week
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const firstDay = getFirstDayOfWeek(date);
    const day = new Date(firstDay);
    day.setDate(firstDay.getDate() + i);
    return day;
  });

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Helper to get task status color
  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "bg-secondary";
      case "In Progress":
        return "bg-primary";
      case "Review":
        return "bg-warning";
      case "Done":
        return "bg-success";
      default:
        return "bg-secondary";
    }
  };

  // Get tasks for a specific day
  const getTasksForDay = (day) => {
    return tasks.filter((task) => {
      const taskDate = new Date(task.updated_at);
      return taskDate.getDate() === day.getDate() && taskDate.getMonth() === day.getMonth() && taskDate.getFullYear() === day.getFullYear();
    });
  };

  // Group tasks by developer for the entire week
  const getTasksByDeveloper = () => {
    // Map of developer IDs to developer info and tasks
    const developerMap = new Map();

    // Add "Unassigned" category
    developerMap.set("unassigned", {
      name: "Unassigned",
      tasksByDay: Array(7)
        .fill()
        .map(() => []),
    });

    // Process all tasks
    tasks.forEach((task) => {
      const taskDate = new Date(task.updated_at);
      const dayIndex = weekDays.findIndex(
        (day) => day.getDate() === taskDate.getDate() && day.getMonth() === taskDate.getMonth() && day.getFullYear() === taskDate.getFullYear()
      );

      // Skip tasks that don't fall within the current week
      if (dayIndex === -1) return;

      if (!task.developers || task.developers.length === 0) {
        // Add to unassigned
        developerMap.get("unassigned").tasksByDay[dayIndex].push(task);
      } else {
        // Add to each developer's tasks
        task.developers.forEach((dev) => {
          const devId = dev.id;
          const devName = dev.name || `${dev.first_name || ""} ${dev.last_name || ""}`.trim() || `ID: ${dev.id}`;

          if (!developerMap.has(devId)) {
            developerMap.set(devId, {
              name: devName,
              tasksByDay: Array(7)
                .fill()
                .map(() => []),
            });
          }

          // Add task to this developer for the specific day
          developerMap.get(devId).tasksByDay[dayIndex].push(task);
        });
      }
    });

    return developerMap;
  };

  // Calculate task position and width based on time
  const calculateTaskPosition = (task) => {
    // For now, we'll use the updated_at time as the start time
    // In a real app, you would have start_time and end_time fields
    const startDate = new Date(task.updated_at);
    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();

    // Simulate a duration (could be from task.duration in real app)
    // Random duration between 1-4 hours for demonstration
    const duration = task.duration || 1 + Math.floor(Math.random() * 4);

    // Calculate position and width
    const startPercent = (startHour + startMinute / 60) * (100 / 24); // Position in day as percentage
    const widthPercent = duration * (100 / 24); // Width based on duration

    return {
      start: startPercent,
      width: widthPercent,
      startTime: `${startHour % 12 || 12}:${startMinute.toString().padStart(2, "0")}${startHour < 12 ? "am" : "pm"}`,
      endTime: `${(startHour + duration) % 12 || 12}:${startMinute.toString().padStart(2, "0")}${
        startHour + duration < 12 || startHour + duration >= 24 ? "am" : "pm"
      }`,
    };
  };

  const developerTasks = getTasksByDeveloper();

  // Working hours for the timeline (e.g., 7am to 7pm)
  const workingHours = Array.from({ length: 13 }, (_, i) => i + 7);

  return (
    <div className="week-view">
      <div className="week-header">
        Week of {weekDays[0].toLocaleDateString("en-US", { month: "long", day: "numeric" })}-{" "}
        {weekDays[6].toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
      </div>

      <div className="week-timeline">
        {/* Day headers */}
        <div className="timeline-days">
          <div className="developer-column">Developer</div>
          {weekDays.map((day, dayIndex) => (
            <div key={dayIndex} className={`day-column ${new Date().toDateString() === day.toDateString() ? "today" : ""}`}>
              {formatDate(day)}
            </div>
          ))}
        </div>

        {/* Developer rows */}
        <div className="timeline-developers">
          {/* Unassigned row */}
          {Array.from(developerTasks.get("unassigned").tasksByDay).some((tasks) => tasks.length > 0) && (
            <div className="developer-row">
              <div className="developer-name">Unassigned</div>

              {weekDays.map((day, dayIndex) => {
                const dayTasks = developerTasks.get("unassigned").tasksByDay[dayIndex];
                const isToday = new Date().toDateString() === day.toDateString();

                return (
                  <div key={dayIndex} className={`day-timeline ${isToday ? "today" : ""}`}>
                    {/* Hour grid lines */}
                    <div className="hour-grid">
                      {workingHours.map((hour) => (
                        <div key={hour} className="hour-line"></div>
                      ))}
                    </div>

                    {/* Tasks */}
                    {dayTasks.map((task) => {
                      const pos = calculateTaskPosition(task);
                      return (
                        <div
                          key={task.id}
                          className={`task-bar ${getStatusColor(task.status)}`}
                          style={{
                            left: `${pos.start}%`,
                            width: `${pos.width}%`,
                          }}
                          onClick={() => onTaskClick(task)}
                          id={`week-task-${task.id}`}
                        >
                          <Tooltip target={`#week-task-${task.id}`} content={`${task.title}: ${task.description || "No description"}`} />
                          <div className="task-time">{pos.startTime}</div>
                          <div className="task-title">{task.title}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Developer rows */}
          {Array.from(developerTasks.entries()).map(([devId, devData]) => {
            if (devId === "unassigned") return null;

            // Check if developer has any tasks this week
            const hasTasks = devData.tasksByDay.some((tasks) => tasks.length > 0);
            if (!hasTasks) return null;

            return (
              <div key={devId} className="developer-row">
                <div className="developer-name">{devData.name}</div>

                {weekDays.map((day, dayIndex) => {
                  const dayTasks = devData.tasksByDay[dayIndex];
                  const isToday = new Date().toDateString() === day.toDateString();

                  return (
                    <div key={dayIndex} className={`day-timeline ${isToday ? "today" : ""}`}>
                      {/* Hour grid lines */}
                      <div className="hour-grid">
                        {workingHours.map((hour) => (
                          <div key={hour} className="hour-line"></div>
                        ))}
                      </div>

                      {/* Tasks */}
                      {dayTasks.map((task) => {
                        const pos = calculateTaskPosition(task);
                        return (
                          <div
                            key={task.id}
                            className={`task-bar ${getStatusColor(task.status)}`}
                            style={{
                              left: `${pos.start}%`,
                              width: `${pos.width}%`,
                            }}
                            onClick={() => onTaskClick(task)}
                            id={`week-task-${task.id}-${dayIndex}`}
                          >
                            <Tooltip
                              target={`#week-task-${task.id}-${dayIndex}`}
                              content={`${task.title}: ${task.description || "No description"}`}
                            />
                            <div className="task-time">{pos.startTime}</div>
                            <div className="task-title">{task.title}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Timeline hours indicator */}
        <div className="timeline-hours-indicator">
          <div className="developer-column"></div>
          <div className="hours-scale">
            {workingHours.map((hour) => (
              <div key={hour} className="hour-label">
                {hour % 12 || 12}
                {hour < 12 ? "am" : "pm"}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .week-view {
          padding: 1rem;
        }

        .week-header {
          margin-bottom: 1.5rem;
          text-align: center;
          font-size: 1.2rem;
          font-weight: 600;
          color: #333;
        }

        .week-timeline {
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          overflow: auto;
        }

        .timeline-days {
          display: flex;
          background-color: #f5f5f5;
          position: sticky;
          top: 0;
          z-index: 10;
          border-bottom: 1px solid #e0e0e0;
        }

        .developer-column {
          width: 150px;
          min-width: 150px;
          padding: 12px 8px;
          font-weight: 600;
          background-color: #f5f5f5;
          border-right: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .day-column {
          flex: 1;
          min-width: 180px;
          padding: 12px 8px;
          text-align: center;
          font-weight: 500;
          border-right: 1px solid #e0e0e0;
        }

        .day-column:last-child {
          border-right: none;
        }

        .day-column.today {
          background-color: #e6f7ff;
        }

        .timeline-developers {
          display: flex;
          flex-direction: column;
        }

        .developer-row {
          display: flex;
          border-bottom: 1px solid #e0e0e0;
          min-height: 100px;
        }

        .developer-row:last-child {
          border-bottom: none;
        }

        .developer-name {
          width: 150px;
          min-width: 150px;
          padding: 8px;
          display: flex;
          align-items: center;
          background-color: #f8f8f8;
          border-right: 1px solid #e0e0e0;
          font-weight: 500;
        }

        .day-timeline {
          flex: 1;
          min-width: 180px;
          height: 100%;
          position: relative;
          border-right: 1px solid #e0e0e0;
        }

        .day-timeline.today {
          background-color: #f9fcff;
        }

        .day-timeline:last-child {
          border-right: none;
        }

        .hour-grid {
          display: flex;
          height: 100%;
          width: 100%;
          position: absolute;
        }

        .hour-line {
          flex: 1;
          border-right: 1px dashed #e0e0e0;
          height: 100%;
        }

        .task-bar {
          position: absolute;
          height: 36px;
          top: 8px;
          border-radius: 4px;
          padding: 3px 6px;
          cursor: pointer;
          color: white;
          overflow: hidden;
          z-index: 1;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .task-bar:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          z-index: 10;
        }

        .task-time {
          font-size: 0.7rem;
          opacity: 0.9;
        }

        .task-title {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .timeline-hours-indicator {
          display: flex;
          background-color: #f5f5f5;
          border-top: 1px solid #e0e0e0;
        }

        .hours-scale {
          flex: 1;
          display: flex;
          padding: 6px 0;
        }

        .hour-label {
          flex: 1;
          min-width: 40px;
          text-align: center;
          font-size: 0.75rem;
          color: #666;
        }

        .calendar-container {
          border: 1px solid #ddd;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        
        .calendar-row {
          display: flex;
          border-bottom: 1px solid #ddd;
          min-height: 85px;
        }
        
        .calendar-header {
          display: flex;
          border-bottom: 1px solid #ddd;
          background-color: #f8f9fa;
        }
        
        .time-column {
          width: 60px;
          min-width: 60px;
          text-align: center;
          padding: 10px 0;
          font-size: 0.85rem;
          color: #666;
          border-right: 1px solid #ddd;
        }
        
        .day-column {
          flex: 1;
          text-align: center;
          padding: 10px 0;
          font-weight: 500;
          border-right: 1px solid #eee;
          min-width: 100px;
        }
        
        .task-item {
          position: absolute;
          height: 65px;
          left: 5px;
          right: 5px;
          padding: 8px 10px;
          border-radius: 4px;
          box-shadow: 0 2px 3px rgba(0,0,0,0.15);
          overflow: hidden;
          cursor: pointer;
          font-size: 0.85rem;
          z-index: 1;
        }
        
        .task-title {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0 0 3px 0;
          font-size: 0.9rem;
        }
        
        .task-time {
          font-size: 0.75rem;
          opacity: 0.95;
          white-space: nowrap;
        }
        
        .task-project {
          font-size: 0.75rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          opacity: 0.9;
          margin-top: 2px;
        }
        
        .task-developer {
          position: absolute;
          bottom: 5px;
          right: 5px;
          font-size: 0.7rem;
          padding: 1px 4px;
          background-color: rgba(0,0,0,0.15);
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};

export default WeekView;
